import { Injectable, inject } from '@angular/core';
import { CountryMetadata, StateMetadata } from '@b2b/types/core';

import { ApiService } from '../api/api.service';

/**
 * LocationService — Angular 18.
 *
 * Ported 1:1 from apps/b2b-ui/common/services/address/location.service.ts
 * (legacy AngularJS name 'LocationService'). Caches AVS country metadata and
 * per-country state metadata, with country/state lookup helpers.
 *
 * The legacy service injected `$q` + `ApiService`. The Angular 18 ApiService
 * facade already returns native promises (`form.avs.countries()` /
 * `form.states.list()`), so `$q` is no longer needed — cache hits return
 * `Promise.resolve(...)` directly.
 *
 * Registered back into AngularJS under 'LocationService' via downgradeInjectable
 * in app/upgrade/ng-downgraded-components.ts. Wave 3 item #25 (address folder).
 */
@Injectable({ providedIn: 'root' })
export class LocationService {
  private apiService = inject(ApiService);

  private countriesCache: CountryMetadata[] = [];
  private statesCache: Record<string, StateMetadata[]> = {};
  private countries: CountryMetadata[] = [];
  private states: StateMetadata[] = [];

  fetchCountriesMetadata(): Promise<CountryMetadata[]> {
    if (this.countriesCache.length > 0) {
      return Promise.resolve(this.countriesCache);
    }

    return this.apiService.form.avs.countries().then((response) => {
      const payload = (response.data as { payload: CountryMetadata[] }).payload;
      this.countriesCache = payload.slice();
      this.countries = payload;
      return payload;
    });
  }

  fetchStatesMetadata(props: { country: string }): Promise<StateMetadata[]> {
    if (this.statesCache[props.country] && this.statesCache[props.country].length > 0) {
      return Promise.resolve(this.statesCache[props.country]);
    }

    return this.apiService.form.states.list(props).then((response) => {
      const payload = (response.data as { payload: StateMetadata[] }).payload;
      this.statesCache[props.country] = payload.slice();
      this.states = payload;
      return payload;
    });
  }

  getCountries() {
    return this.countries;
  }

  getStates() {
    return this.states;
  }

  findCountry(item: { country: string | undefined }) {
    for (let i = 0, ii = this.countries.length; i < ii; i++) {
      if (
        this.countries[i].name.toLowerCase() === item.country?.toLowerCase() ||
        this.countries[i].alpha2Code === item.country ||
        this.countries[i].alpha3Code === item.country
      ) {
        return {
          index: i,
          country: this.countries[i],
        };
      }
    }

    return undefined;
  }

  findState(item: { state: string | undefined }) {
    if (item && item.state && this.states.length) {
      return this.states.find((state) => {
        return (
          item.state?.toLowerCase() === state.abbreviation.toLowerCase() ||
          item.state?.toLowerCase() === state.name.toLowerCase()
        );
      });
    }

    return undefined;
  }
}
